import { createApi } from "@reduxjs/toolkit/query/react";
import {
  listDevicesResponse,
  Device,
  GPS,
  scheduleDeviceUpdateResponse,
  displayNameUpdateResponse,
} from "../../types";
import { axiosBaseQuery } from "../../providers/auth0-instance";
import { withErrorNotification } from "../notifications";

export const deviceApi = createApi({
  reducerPath: "deviceApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Device"],
  endpoints: (builder) => ({
    // `org` is optional and only the superuser panel passes it; it is part of
    // the query arg so the cache key differs per organization.
    listDevices: builder.query<listDevicesResponse, { org?: string } | void>({
      query: (args) => ({ url: "/devices", org: args?.org }),
      providesTags: ["Device"],
      onQueryStarted: withErrorNotification("Failed to list devices"),
    }),
    getDeviceById: builder.query<Device, string>({
      query: (deviceUuid) => ({ url: `/device/${deviceUuid}` }),
      providesTags: (result, error, id) => [{ type: "Device", id }],
      // Only ever fired by picking a device in DeviceSelector, or by
      // DeviceSetup off a `selected` that a pick already produced - never by
      // the opening burst.
      onQueryStarted: withErrorNotification("Failed to fetch device details", {
        alwaysNotify: true,
      }),
    }),
    scheduleDeviceUpdate: builder.mutation<
      scheduleDeviceUpdateResponse,
      { deviceUuid: string; will_update: boolean }
    >({
      query: ({ deviceUuid, ...data }) => ({
        url: `/device/${deviceUuid}/firmware`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (result, error, { deviceUuid }) => [
        { type: "Device", id: deviceUuid },
        "Device",
      ],
      onQueryStarted: withErrorNotification(
        "Failed to schedule device update",
        {
          alwaysNotify: true,
        },
      ),
    }),
    /**
     * A device's own editable fields. Every one is optional, so a caller can
     * move a trap without touching its name.
     *
     * `gps: null` clears the device's own position and `trap_site_id: null`
     * unassigns it, so both are sent explicitly rather than omitted when the
     * intent is to clear them.
     */
    updateDevice: builder.mutation<
      displayNameUpdateResponse,
      {
        deviceUuid: string;
        display_name?: string;
        gps?: GPS | null;
        trap_site_id?: string | null;
      }
    >({
      query: ({ deviceUuid, ...data }) => ({
        url: `/device/${deviceUuid}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (result, error, { deviceUuid }) => [
        { type: "Device", id: deviceUuid },
        "Device",
      ],
      onQueryStarted: withErrorNotification("Failed to update device", {
        alwaysNotify: true,
      }),
    }),
    displayNameUpdate: builder.mutation<
      displayNameUpdateResponse,
      { deviceUuid: string; display_name: string }
    >({
      query: ({ deviceUuid, ...data }) => ({
        url: `/device/${deviceUuid}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (result, error, { deviceUuid }) => [
        { type: "Device", id: deviceUuid },
        "Device",
      ],
      onQueryStarted: withErrorNotification("Failed to update display name", {
        alwaysNotify: true,
      }),
    }),
    deviceWifiUpdate: builder.mutation<
      displayNameUpdateResponse,
      { deviceUuid: string; wifi: string }
    >({
      query: ({ deviceUuid, ...data }) => ({
        url: `/device/${deviceUuid}/wifi`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (result, error, { deviceUuid }) => [
        { type: "Device", id: deviceUuid },
        "Device",
      ],
      onQueryStarted: withErrorNotification("Failed to update wifi", {
        alwaysNotify: true,
      }),
    }),
  }),
});

export const {
  useListDevicesQuery,
  useGetDeviceByIdQuery,
  useLazyGetDeviceByIdQuery,
  useScheduleDeviceUpdateMutation,
  useDisplayNameUpdateMutation,
  useUpdateDeviceMutation,
  useDeviceWifiUpdateMutation,
} = deviceApi;
